import {LoginOutlined, LogoutOutlined, MenuOutlined, ProductOutlined, SearchOutlined, UserOutlined} from '@ant-design/icons';
import {Badge, Button, Dropdown, Input} from 'antd';
import {useContext, useEffect, useRef, useState} from 'react';
import {FaCartArrowDown} from 'react-icons/fa';
import {PiHandbagBold, PiHandbagLight, PiShoppingCartBold} from 'react-icons/pi';
import {RiAccountBoxLine, RiDashboardHorizontalLine} from 'react-icons/ri';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import logo from '../../assets/logo.png';
import {RQGetCategoryAPI} from '../../reactQuery/apis';
import {logoutStart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';
import {LoginScreen} from '../LoginScreen';

import {FilterContext} from './../../context/FilterContext/FilterContext';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const {setFilters} = useContext(FilterContext);
  const dispatch = useDispatch();
  const skipRedirectRef = useRef(false);

  const auth = useSelector(selectAuth);
  const loggedIn = auth?.loggedIn;
  const userRole = auth?.user?.role;

  const {totalQuantity} = useSelector(state => state.cart); 

  const {data: categoryData = [], isLoading} = RQGetCategoryAPI();

  const categoryOptions = categoryData
    .filter(category => category.isActive)
    .map(category => ({
      value: category.name, 
      label: category.name,
    }));

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchFilter, setSearchFilter] = useState('');
  const [isUserSearching, setIsUserSearching] = useState(false);

  const location = useLocation();
  const isOnProductPage = location.pathname === '/product';
  const isProductDetailsPage = location.pathname.startsWith('/product/');

  useEffect(() => {
    const search = searchParams.get('search');

    if (search) {
      setSearchFilter(search);
    } else if (isOnProductPage) {
      setSearchFilter('');
    } else {
      setSearchFilter('');
      setIsUserSearching(false);
    }
  }, [searchParams, isOnProductPage, location.pathname]);

  useEffect(() => {
    if (!isUserSearching) {return;}
    if (isProductDetailsPage) {return;} 

    const handler = setTimeout(() => {
      const value = searchFilter.trim();

      if (value) {
        navigate(`/product?search=${encodeURIComponent(value)}`);
      } else if (isOnProductPage) {
        navigate('/product');
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [
    searchFilter,
    isUserSearching,
    isOnProductPage,
    isProductDetailsPage,
    navigate,
  ]);

  const handleSearchChange = e => {
    setIsUserSearching(true);
    setSearchFilter(e.target.value);
  };

  useEffect(() => {
    const closeMenu = () => setShowAllCategories(false);
    document.addEventListener('click', closeMenu);

    return () => document.removeEventListener('click', closeMenu);
  }, []);
  const isAdmin = Boolean(
    loggedIn && userRole && userRole.toLowerCase() === 'admin',
  );
  const handleLogout = () => {
    skipRedirectRef.current = true;
    dispatch(logoutStart());
    navigate('/');
  };
  const accountMenu = {
    items: [
      {
        key: 'profile',
        label: (
          <NavLink className={'!text-primary-900 font-medium'} to={'/userhistory'}>
            <div className={'flex items-center gap-2'}>
              <RiAccountBoxLine className={'text-lg'} />
            Profile
            </div>
          </NavLink>
        ),
      }, 
      {
        key: 'order',
        label: (
          <NavLink
            className={'!text-primary-900 font-medium'}
            to={'/userhistory?tab=orders'}
            replace
          >
            <div className={'flex items-center gap-2'}>
              <FaCartArrowDown  />
        orders
            </div>
          </NavLink>
        ),
      },
      {
        key: 'logout',
        label: (
          <button
            onClick={handleLogout}
            className={'flex text-primary-900 font-medium items-center gap-2 w-full text-left bg-transparent border-none cursor-pointer'}
          >
            <LogoutOutlined />
      Logout
          </button>
        ),
      },
      
    ],
  };
  const handleProfileClick = e => {
    if (!loggedIn) {
      e.preventDefault();
      setShowLoginModal(true);
    }
  };
  useEffect(() => {
    if (!auth.loggedIn) {
      dispatch(logoutStart({showLoginModal: true}));
    }
  }, [auth.loggedIn, dispatch]);

  const goToCategory = categoryName => {
    setIsUserSearching(false); // Add this
    navigate(`/product?category=${encodeURIComponent(categoryName)}`);
    setShowAllCategories(false);
    setOpen(false);
  };

  return (
    <div className={'w-full md:mb-32 mb-24'}>
      <nav
        className={`fixed w-full z-20 bg-white transition-all ${
          isScrolled ? 'pt-2 shadow-lg' : 'pt-3'
        }`}
      >
        <div className={'max-w-full mx-auto md:px-16 px-4'}>
          <div className={'flex w-full items-center justify-between'}>
            {/* Logo */}
            <div>
              <NavLink to={'/'}>
                <img
                  src={logo}
                  alt={'Logo'}
                  className={isScrolled ? 'md:h-10 h-8' : 'md:h-16 h-10'}
                />
              </NavLink>
            </div>

            {/* Desktop Search bar */}
            <div className={'hidden md:flex w-full justify-center flex-1 '}>
              <Input
                placeholder={'Search products'}
                allowClear
                value={searchFilter}
                onChange={handleSearchChange}
                className={'w-[700px]'}
                suffix={(
                  <Button className={'bg-primary-900 hover:!bg-primary-950 border-none'}>
                    <SearchOutlined className={'text-white text-lg'} />
                  </Button>
                )}
              />
            </div>

            {/* Desktop Navigation */}
            <div className={'hidden md:flex items-start gap-6 text-primary-950 text-xl'}>

              {/* Product */}
              <NavLink to={'/product'} className={'flex items-center gap-1'}>
                <PiHandbagBold />
                <span className={'text-sm font-semibold'}>Product</span>
              </NavLink>

              {/* Cart */}
              {loggedIn && (
                <NavLink to={'/cart'} className={'flex items-center gap-1'}>
                  <PiShoppingCartBold  className={'font-bold mb-2'}   />
                  <span className={'text-sm mb-2 font-semibold'}>Cart</span>
                  <Badge className={''} style={{backgroundColor: '#233f82'}} count={totalQuantity} size={'small'} offset={[5, -2]}>
                    <span className={'text-lg mr-1 text-white'}></span>
                  </Badge>

                </NavLink>
              )}

              {loggedIn && isAdmin && (
                <NavLink to={'/admin/dashboard'} className={'flex  items-center gap-1'}>
                  <RiDashboardHorizontalLine />
                  <span className={'text-sm font-semibold'}>Dashboard</span>
                </NavLink>
              )}

              {loggedIn && !isAdmin && (
                <Dropdown className={''} menu={accountMenu} placement={'bottomRight'}>
                  <button className={'flex items-center gap-1 bg-transparent border-none cursor-pointer'}>
                    <UserOutlined />
                    <span className={'text-sm font-semibold'}>Account</span>
                  </button>
                </Dropdown>
              )}

              {!loggedIn && (
                <NavLink
                  to={'#'}
                  onClick={handleProfileClick}
                  className={'flex items-center gap-1'}
                >
                  <LoginOutlined />
                  <span className={'text-sm font-semibold'}>Login</span>
                </NavLink>
              )}

            </div>

            {/* Mobile Navigation */}
            <div className={'flex items-center gap-4 md:hidden text-primary-950 text-xl'}>
              <NavLink to={'/product'} className={'flex items-center gap-1'}>
                <PiHandbagBold   />
              </NavLink>
              
              {loggedIn && (
                <NavLink to={'/cart'} className={'flex items-center gap-1'}>
                  <PiShoppingCartBold   className={'font-bold'}   />
                  <Badge className={''} style={{backgroundColor: '#233f82'}} count={totalQuantity} size={'small'} offset={[5, -2]}>
                    <span className={'text-lg mr-1 text-white'}></span>
                  </Badge>

                </NavLink>
              )}
              {loggedIn && isAdmin && (
                <NavLink to={'/admin/dashboard'} className={'flex items-center gap-1'}>
                  <RiDashboardHorizontalLine />
                  <span className={'text-sm font-semibold'}></span>
                </NavLink>
              )}

              {loggedIn && !isAdmin && (
                <Dropdown menu={accountMenu} placement={'bottomRight'}>
                  <button className={'flex items-center gap-1 bg-transparent border-none cursor-pointer'}>
                    <UserOutlined />
                    <span className={'text-sm font-semibold'}></span>
                  </button>
                </Dropdown>
              )}

              {!loggedIn && (
                <NavLink
                  to={'#'}
                  onClick={handleProfileClick}
                  className={'flex items-center gap-1'}
                >
                  <LoginOutlined />
                  <span className={'text-sm font-semibold'}></span>
                </NavLink>
              )}

            </div>
          </div>

          {/*  Mobile Search bar  */}
          <div className={'mt-3 md:hidden'}>
            <Input
              placeholder={'Search products'}
              allowClear
              value={searchFilter}
              onChange={handleSearchChange}
              suffix={(
                <Button className={'bg-primary-800 border-none'}>
                  <SearchOutlined className={'text-white text-lg'} />
                </Button>
              )}
            />
          </div>
        </div>

        {/*  CATEGORY BAR  */}
        <div className={'bg-primary-950 h-12 flex items-center mt-3'}>
          <div className={'relative px-2'}>
            <button
              className={'flex items-center gap-2 font-semibold text-primary-50 h-full'}
              onClick={e => {
                e.stopPropagation();
                setShowAllCategories(prev => !prev);
              }}
            >
              <MenuOutlined />
              <span>All</span>
            </button>

            {showAllCategories && (
              <div
                className={
                  'absolute top-full left-0 w-56 bg-primary-950 text-primary-50 shadow-lg z-50 mt-3  max-h-[500px] overflow-y-auto'
                }
              >
                {isLoading ? (
                  <div className={'px-4 py-2 text-primary-50'}>Loading...</div>
                ) : (
                  categoryOptions.map(category => (
                    <button
                      key={category.value}
                      className={
                        'block w-full text-left px-4 py-2 pl-8 hover:bg-gray-200 hover:text-primary-50'
                      }
                      onClick={() => goToCategory(category.value)}
                    >
                      {category.label}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
         
          <div className={'flex-1 overflow-x-auto whitespace-nowrap h-full flex items-center'}>
            <div className={'flex items-center gap-4 px-2'}>
              {isLoading ? (
                <span className={'text-gray-300 px-2'}>Loading...</span>
              ) : (
                categoryOptions.map(category => (
                  <button
                    key={category.value}
                    onClick={() => goToCategory(category.value)}
                    className={'text-primary-50 px-2 py-1 text-sm hover:underline'}
                  >
                    {category.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </nav>
      {showLoginModal && (
        <LoginScreen onClose={() => setShowLoginModal(false)} />
      )}
    </div>
  );
};

export default Navbar;
